import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  InitialState,
  CustomersResponse,
  BookingBeneficiaryDetails,
  CustomerAccount,
  CustomerProfile,
} from "./types";
import {
  fetchCustomerDetailsAction,
  fetchAllCustomers,
  fetchFilteredCustomersAction,
  fetchCustomerBookingsAction,
  fetchFilteredProfilesAction,
} from "./action";

const initialBenficiaryDetails = {
  title: "",
  firstName: "",
  lastName: "",
  email: "",
  gender: "",
  dateOfBirth: "",
  mobile: "",
};

const initialState: InitialState = {
  loading: false,
  allCustomers: [],
  customerData: {
    data: [],
    total: 0,
  },
  customerDetails: null,
  customerBookings: [],
  error: false,
  message: null,
  bookingBeneficiaryDetails: initialBenficiaryDetails,
  customerProfilesData: {
    profiles: [],
    total: 0,
  },
};

const customerSlices = createSlice({
  name: "customer",
  initialState,
  reducers: {
    saveBeneficiaryDetails: (
      state,
      data: { type: string; payload: BookingBeneficiaryDetails }
    ) => {
      state.bookingBeneficiaryDetails = data.payload;
    },
  },
  extraReducers: (builder) => {
    // find filtered customers
    builder.addCase(fetchFilteredCustomersAction.pending, (state) => {
      return {
        ...state,
        loading: false,
      };
    });

    builder.addCase(fetchFilteredCustomersAction.fulfilled, (state, action) => {
      const { payload } = action.payload;
      return {
        ...state,
        loading: false,
        customerData: {
          data: payload?.data,
          total: payload?.total,
        },
      };
    });

    builder.addCase(fetchFilteredCustomersAction.rejected, (state, action) => {
      return {
        ...state,
        loading: false,
        customerDetails: null,
      };
    });

    builder.addCase(fetchAllCustomers.pending, (state) => {
      return {
        ...state,
        loading: false,
      };
    });

    builder.addCase(fetchAllCustomers.fulfilled, (state, action) => {
      const { payload } = action.payload;
      return {
        ...state,
        loading: false,
        allCustomers: payload,
      };
    });

    builder.addCase(fetchAllCustomers.rejected, (state, action) => {
      return {
        ...state,
        loading: false,
        allCustomers: [],
      };
    });

    // find one customer
    builder.addCase(fetchCustomerDetailsAction.pending, (state) => {
      return {
        ...state,
        loading: false,
      };
    });
    builder.addCase(fetchCustomerDetailsAction.fulfilled, (state, action) => {
      const { payload } = action.payload;
      return {
        ...state,
        loading: false,
        customerDetails: payload,
        error: false,
      };
    });

    builder.addCase(fetchCustomerDetailsAction.rejected, (state, action) => {
      return {
        ...state,
        loading: false,
        customerDetails: null,
        error: true,
      };
    });

    // find customer bookings
    builder.addCase(fetchCustomerBookingsAction.pending, (state) => {
      return {
        ...state,
        loading: false,
      };
    });
    builder.addCase(fetchCustomerBookingsAction.fulfilled, (state, action) => {
      const { payload } = action.payload;
      return {
        ...state,
        loading: false,
        customerBookings: payload,
        error: false,
      };
    });

    builder.addCase(fetchCustomerBookingsAction.rejected, (state, action) => {
      return {
        ...state,
        loading: false,
        customerBookings: [],
        error: true,
      };
    });

    // find filtered profiles
    builder.addCase(fetchFilteredProfilesAction.pending, (state) => {
      return {
        ...state,
        loading: false,
      };
    });

    builder.addCase(fetchFilteredProfilesAction.fulfilled, (state, action) => {
      const { payload } = action.payload;
      return {
        ...state,
        loading: false,
        customerProfilesData: {
          profiles: payload?.profiles,
          total: payload?.total,
        },
      };
    });

    builder.addCase(fetchFilteredProfilesAction.rejected, (state, action) => {
      return {
        ...state,
        loading: false,
        customerDetails: null,
      };
    });
  },
});

export const { saveBeneficiaryDetails } = customerSlices.actions;
export default customerSlices.reducer;
